import { db } from '../firebase'
import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where,
  orderBy,
  serverTimestamp 
} from 'firebase/firestore'

/**
 * Appointment Service - Firestore Integration
 * Manages appointments, availability, and meeting types in Firestore
 */

class AppointmentFirestoreService {
  constructor() {
    this.appointmentsCollection = 'appointments'
    this.availabilityCollection = 'availability_settings'
    this.meetingTypesCollection = 'meeting_types'
  }

  /**
   * Get all appointments for a tenant
   */
  async getAppointments(tenantId) {
    try {
      const appointmentsRef = collection(db, this.appointmentsCollection)
      const q = query(
        appointmentsRef,
        where('tenantId', '==', tenantId),
        orderBy('startTime', 'desc')
      )
      
      const querySnapshot = await getDocs(q)
      const appointments = []
      
      querySnapshot.forEach((doc) => {
        appointments.push({
          id: doc.id,
          ...doc.data()
        })
      })
      
      return appointments
    } catch (error) {
      console.error('Error fetching appointments:', error)
      throw error
    }
  }

  /**
   * Get single appointment by ID
   */
  async getAppointmentById(appointmentId) {
    try {
      const appointmentRef = doc(db, this.appointmentsCollection, appointmentId)
      const appointmentSnap = await getDoc(appointmentRef)
      
      if (appointmentSnap.exists()) {
        return {
          id: appointmentSnap.id,
          ...appointmentSnap.data()
        }
      } else {
        return null
      }
    } catch (error) {
      console.error('Error fetching appointment:', error)
      throw error
    }
  }

  /**
   * Create new appointment
   */
  async createAppointment(tenantId, appointmentData) {
    try {
      const appointmentsRef = collection(db, this.appointmentsCollection)
      const docRef = await addDoc(appointmentsRef, {
        ...appointmentData,
        tenantId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      })
      
      return {
        id: docRef.id,
        ...appointmentData,
        tenantId
      }
    } catch (error) {
      console.error('Error creating appointment:', error)
      throw error
    }
  }

  /**
   * Update existing appointment
   */
  async updateAppointment(appointmentId, updates) {
    try {
      const appointmentRef = doc(db, this.appointmentsCollection, appointmentId)
      await updateDoc(appointmentRef, {
        ...updates,
        updatedAt: serverTimestamp()
      })
      
      return {
        id: appointmentId,
        ...updates
      }
    } catch (error) {
      console.error('Error updating appointment:', error)
      throw error
    }
  }

  /**
   * Delete appointment
   */
  async deleteAppointment(appointmentId) {
    try {
      const appointmentRef = doc(db, this.appointmentsCollection, appointmentId)
      await deleteDoc(appointmentRef)
      return true
    } catch (error) {
      console.error('Error deleting appointment:', error)
      throw error
    }
  }

  /**
   * Get availability settings for a tenant
   */
  async getAvailability(tenantId) {
    try {
      const availabilityRef = doc(db, this.availabilityCollection, tenantId)
      const availabilitySnap = await getDoc(availabilityRef)
      
      if (availabilitySnap.exists()) {
        return availabilitySnap.data()
      } else {
        // Return default availability
        return this.getDefaultAvailability()
      }
    } catch (error) {
      console.error('Error fetching availability:', error)
      throw error
    }
  }

  /**
   * Update availability settings
   */
  async updateAvailability(tenantId, availabilityData) {
    try {
      const availabilityRef = doc(db, this.availabilityCollection, tenantId)
      await updateDoc(availabilityRef, {
        ...availabilityData,
        updatedAt: serverTimestamp()
      })
      return availabilityData
    } catch (error) {
      // If document doesn't exist, create it
      if (error.code === 'not-found') {
        const availabilityRef = doc(db, this.availabilityCollection, tenantId)
        await addDoc(availabilityRef, {
          ...availabilityData,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        })
        return availabilityData
      }
      console.error('Error updating availability:', error)
      throw error
    }
  }

  /**
   * Get meeting types for a tenant
   */
  async getMeetingTypes(tenantId) {
    try {
      const meetingTypesRef = collection(db, this.meetingTypesCollection)
      const q = query(
        meetingTypesRef,
        where('tenantId', '==', tenantId)
      )
      
      const querySnapshot = await getDocs(q)
      const meetingTypes = []
      
      querySnapshot.forEach((doc) => {
        meetingTypes.push({
          id: doc.id,
          ...doc.data()
        })
      })
      
      // Return default meeting types if none exist
      if (meetingTypes.length === 0) {
        return this.getDefaultMeetingTypes()
      }
      
      return meetingTypes
    } catch (error) {
      console.error('Error fetching meeting types:', error)
      throw error
    }
  }

  /**
   * Create or update meeting type
   */
  async saveMeetingType(tenantId, meetingTypeData) {
    try {
      if (meetingTypeData.id) {
        // Update existing
        const meetingTypeRef = doc(db, this.meetingTypesCollection, meetingTypeData.id)
        await updateDoc(meetingTypeRef, {
          ...meetingTypeData,
          updatedAt: serverTimestamp()
        })
        return meetingTypeData
      } else {
        // Create new
        const meetingTypesRef = collection(db, this.meetingTypesCollection)
        const docRef = await addDoc(meetingTypesRef, {
          ...meetingTypeData,
          tenantId,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        })
        return {
          id: docRef.id,
          ...meetingTypeData
        }
      }
    } catch (error) {
      console.error('Error saving meeting type:', error)
      throw error
    }
  }

  /**
   * Delete meeting type
   */
  async deleteMeetingType(meetingTypeId) {
    try {
      const meetingTypeRef = doc(db, this.meetingTypesCollection, meetingTypeId)
      await deleteDoc(meetingTypeRef)
      return true
    } catch (error) {
      console.error('Error deleting meeting type:', error)
      throw error
    }
  }

  /**
   * Default availability (9am-5pm weekdays)
   */
  getDefaultAvailability() {
    return {
      monday: { enabled: true, start: '09:00', end: '17:00' },
      tuesday: { enabled: true, start: '09:00', end: '17:00' },
      wednesday: { enabled: true, start: '09:00', end: '17:00' },
      thursday: { enabled: true, start: '09:00', end: '17:00' },
      friday: { enabled: true, start: '09:00', end: '17:00' },
      saturday: { enabled: false, start: '09:00', end: '17:00' },
      sunday: { enabled: false, start: '09:00', end: '17:00' }
    }
  }

  /**
   * Default meeting types
   */
  getDefaultMeetingTypes() {
    return [
      {
        id: 'quick-call',
        name: '15-Minute Quick Call',
        duration: 15,
        description: 'Quick chat to discuss your needs',
        color: '#3b82f6',
        active: true
      },
      {
        id: 'consultation',
        name: '30-Minute Consultation',
        duration: 30,
        description: 'In-depth consultation session',
        color: '#8b5cf6',
        active: true
      },
      {
        id: 'discovery',
        name: '60-Minute Discovery Call',
        duration: 60,
        description: 'Full discovery and strategy session',
        color: '#10b981',
        active: true
      }
    ]
  }

  /**
   * Get booking settings for a tenant
   */
  async getBookingSettings(tenantId) {
    try {
      const bookingSettingsRef = doc(db, 'booking_settings', tenantId)
      const bookingSettingsSnap = await getDoc(bookingSettingsRef)
      
      if (bookingSettingsSnap.exists()) {
        return bookingSettingsSnap.data()
      } else {
        return this.getDefaultBookingSettings()
      }
    } catch (error) {
      console.error('Error fetching booking settings:', error)
      throw error
    }
  }

  /**
   * Update booking settings
   */
  async updateBookingSettings(tenantId, bookingSettingsData) {
    try {
      const bookingSettingsRef = doc(db, 'booking_settings', tenantId)
      const docSnap = await getDoc(bookingSettingsRef)
      
      if (docSnap.exists()) {
        await updateDoc(bookingSettingsRef, {
          ...bookingSettingsData,
          updatedAt: serverTimestamp()
        })
      } else {
        await addDoc(collection(db, 'booking_settings'), {
          ...bookingSettingsData,
          tenantId,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        })
      }
      return bookingSettingsData
    } catch (error) {
      console.error('Error updating booking settings:', error)
      throw error
    }
  }

  /**
   * Default booking settings
   */
  getDefaultBookingSettings() {
    return {
      bufferTime: 15,
      advanceBooking: 30,
      minNotice: 120,
      timezone: 'America/New_York',
      confirmationEmail: true,
      reminderEmail24h: true,
      reminderEmail1h: true,
      requirePhone: false,
      requireCompany: true,
      customQuestions: []
    }
  }

  /**
   * Migrate appointments from localStorage to Firestore
   */
  async migrateFromLocalStorage(tenantId) {
    try {
      const localKey = `appointments_${tenantId || 'default'}`
      const localData = localStorage.getItem(localKey)
      
      if (!localData) {
        console.log('No local appointments to migrate')
        return { migrated: 0 }
      }

      const appointments = JSON.parse(localData)
      let migrated = 0

      for (const appointment of appointments) {
        await this.createAppointment(tenantId, appointment)
        migrated++
      }

      console.log(`Successfully migrated ${migrated} appointments to Firestore`)
      return { migrated }
    } catch (error) {
      console.error('Error migrating appointments:', error)
      throw error
    }
  }
}

export default new AppointmentFirestoreService()
